<template>
  <LoadingWrapper :state="status">
    <SideBarLayoutContent :small-bottom-padding="true">
      <template #top-bar>
        <div class="flex grow flex-row items-start">
          <h1 class="text-headline-large grow">{{ $t('mainNavigation.profile') }}</h1>
          <BaseIconButton
            v-tooltip.bottom="$t('profile.download.tooltip')"
            icon="download"
            size="M"
            width="narrow"
            color="outlined"
            @click="openExportDialog"
            :loading="isDownloading"
          />
        </div>
      </template>
      <div class="flex w-full flex-col pb-8">
        <SuperpowerProfile v-if="superpower" :superpower="superpower" class="mb-6" />

        <BaseTabGroup v-if="feedbackTopics.length > 0" :tab-items="tabItems">
          <BaseTabBar :tab-items="tabItems" class="mb-4 border-b" />
          <TabPanels>
            <TabPanel v-for="feedbackTopic in feedbackTopics" :key="feedbackTopic.id">
              <EvaluationProfile
                v-if="feedbackTopic.type == FeedbackTopicType.Evaluation"
                :feedback-topic-id="feedbackTopic.id"
              />
              <PulseProfile
                v-if="feedbackTopic.type == FeedbackTopicType.Pulse"
                :feedback-topic-id="feedbackTopic.id"
              />
              <JournalProfile
                v-if="feedbackTopic.type == FeedbackTopicType.Journal"
                :feedback-topic-id="feedbackTopic.id"
              />
            </TabPanel>
          </TabPanels>
        </BaseTabGroup>
        <EmptyPage
          v-if="feedbackTopics.length == 0 && !superpower"
          :title="$t('profile.emptyDescription')"
          :description="$t('profile.emptyCTA')"
          icon="widgets"
        >
          <BaseTextButton color="filled" icon="add" size="M" @click="startSurvey">{{
            $t('survey.actions.create')
          }}</BaseTextButton></EmptyPage
        >
      </div>
      <ProfileExportDialog
        :is-open="isExportDialogOpen"
        @close="closeExportDialog"
        @submit="handleCreateExportPayload"
      />
    </SideBarLayoutContent>
  </LoadingWrapper>
</template>

<script setup lang="ts">
  import SideBarLayoutContent from '../../../core/components/layouts/SideBarLayoutContent.vue'
  import LoadingWrapper from '../../../core/components/loading/LoadingWrapper.vue'
  import { useProfile } from '../composables/useProfile'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import EmptyPage from '../../../core/components/empty-states/EmptyPage.vue'
  import { computed } from 'vue'
  import { useRouter } from 'vue-router'
  import { FeedbackTopicType } from '@/core/models/feedback-topic'
  import EvaluationProfile from '../components/EvaluationProfile.vue'
  import PulseProfile from '../components/PulseProfile.vue'
  import JournalProfile from '../components/JournalProfile.vue'
  import SuperpowerProfile from '../components/SuperpowerProfile.vue'
  import BaseTabBar from '@/core/components/base/tab-bar/BaseTabBar.vue'
  import BaseTabGroup from '@/core/components/base/tab-bar/BaseTabGroup.vue'
  import { TabPanel, TabPanels } from '@headlessui/vue'
  import { useDialog } from '@/core/components/base/dialog/use-dialog'
  import ProfileExportDialog from '../components/ProfileExportDialog.vue'
  import type { ProfilePdfPayload } from '../composables/use-profile-export-options'
  import { useFileDownload } from '@/core/composables/use-download-file'
  import { FetchMethod } from '@/core/models/api/fetch-method'
  import BaseIconButton from '@/core/components/base/button/icon-button/BaseIconButton.vue'
  const { feedbackTopics, superpower, status } = await useProfile()

  const tabItems = computed(() =>
    feedbackTopics.value.map((feedbackTopic) => ({
      id: feedbackTopic.id,
      title: feedbackTopic.title,
      icon: feedbackTopic.icon,
    })),
  )

  const router = useRouter()
  const {
    isOpen: isExportDialogOpen,
    openDialog: openExportDialog,
    closeDialog: closeExportDialog,
  } = useDialog()

  const startSurvey = () => {
    router.push({ query: { requestDialog: '1' } })
  }

  const { downloadFile, isDownloading } = useFileDownload(`profile/pdf`)
  const handleCreateExportPayload = (payload: ProfilePdfPayload) => {
    downloadFile({
      method: FetchMethod.POST,
      body: payload,
    })
  }
</script>
